import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    projectType: '',
    message: ''
  });

  const [activeForm, setActiveForm] = useState('contact');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowError(false);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, contactForm);
      console.log('Contact form submitted:', response.data);
      setShowSuccess(true);
      setContactForm({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setErrorMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowError(false);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/quote`, quoteForm);
      console.log('Quote form submitted:', response.data);
      setShowSuccess(true);
      setQuoteForm({ name: '', email: '', phone: '', address: '', projectType: '', message: '' });
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting quote form:', error);
      setErrorMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-orange-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#03224c] mb-6">
              Contactez-nous
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Une question ? Un projet ? Notre équipe est à votre écoute pour vous 
              accompagner dans votre transition énergétique en Suisse romande.
            </p>
          </div>
        </div>
      </section>

      {/* Success Message */}
      {showSuccess && (
        <div className="container mx-auto px-4 py-4">
          <div className="max-w-4xl mx-auto bg-green-50 border-2 border-green-500 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="text-green-500" size={24} />
              <div>
                <div className="font-bold text-green-800">Message envoyé avec succès !</div>
                <div className="text-sm text-green-700">Nous vous répondrons dans les plus brefs délais.</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {showError && (
        <div className="container mx-auto px-4 py-4">
          <div className="max-w-4xl mx-auto bg-red-50 border-2 border-red-500 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="text-red-500 text-xl">⚠️</div>
              <div>
                <div className="font-bold text-red-800">Erreur</div>
                <div className="text-sm text-red-700">{errorMessage}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg text-center border-2 border-gray-200 hover:border-[#e57200] transition-all">
              <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                <Phone className="text-[#e57200]" size={28} />
              </div>
              <h3 className="font-bold text-[#03224c] mb-3 text-lg">Téléphone</h3>
              <div className="space-y-2">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="block text-gray-600 hover:text-[#e57200] transition-colors font-semibold"
                >
                  {companyInfo.phone}
                </a>
                <a
                  href={`tel:${companyInfo.mobile}`}
                  className="block text-gray-600 hover:text-[#e57200] transition-colors font-semibold"
                >
                  {companyInfo.mobile}
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg text-center border-2 border-gray-200 hover:border-[#e57200] transition-all">
              <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                <Mail className="text-[#e57200]" size={28} />
              </div>
              <h3 className="font-bold text-[#03224c] mb-3 text-lg">Email</h3>
              <a
                href={`mailto:${companyInfo.email}`}
                className="text-gray-600 hover:text-[#e57200] transition-colors font-semibold"
              >
                {companyInfo.email}
              </a>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg text-center border-2 border-gray-200 hover:border-[#e57200] transition-all">
              <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-[#e57200]" size={28} />
              </div>
              <h3 className="font-bold text-[#03224c] mb-3 text-lg">Adresse</h3>
              <p className="text-gray-600 font-semibold">{companyInfo.address}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Form Tabs */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex rounded-xl bg-white shadow-lg p-2">
                <button
                  onClick={() => setActiveForm('contact')}
                  className={`px-8 py-3 rounded-lg font-bold transition-all ${
                    activeForm === 'contact'
                      ? 'bg-[#e57200] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Contact simple
                </button>
                <button
                  onClick={() => setActiveForm('quote')}
                  className={`px-8 py-3 rounded-lg font-bold transition-all ${
                    activeForm === 'quote'
                      ? 'bg-[#e57200] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Demande de devis
                </button>
              </div>
            </div>

            {/* Contact Form */}
            {activeForm === 'contact' && (
              <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
                <h2 className="text-3xl font-bold text-[#03224c] mb-8">Envoyez-nous un message</h2>
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Nom complet *</label>
                      <input
                        type="text"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder="Jean Dupont"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder="jean.dupont@example.com"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Téléphone</label>
                    <input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="079 123 45 67"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Message *</label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="Décrivez votre demande..."
                      rows={6}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-[#e57200] text-white rounded-lg font-bold text-lg hover:bg-[#e57200]/90 transition-all shadow-lg hover:shadow-xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                    {!isSubmitting && <Send className="ml-2" size={20} />}
                  </button>
                </form>
              </div>
            )}

            {/* Quote Form */}
            {activeForm === 'quote' && (
              <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
                <h2 className="text-3xl font-bold text-[#03224c] mb-4">Demande de devis gratuit</h2>
                <p className="text-gray-600 mb-8">
                  Remplissez ce formulaire pour recevoir une étude personnalisée et un devis gratuit
                </p>
                <form onSubmit={handleQuoteSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Nom complet *</label>
                      <input
                        type="text"
                        value={quoteForm.name}
                        onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                        placeholder="Jean Dupont"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                        placeholder="jean.dupont@example.com"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Téléphone *</label>
                      <input
                        type="tel"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                        placeholder="079 123 45 67"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Adresse du projet *</label>
                      <input
                        type="text"
                        value={quoteForm.address}
                        onChange={(e) => setQuoteForm({ ...quoteForm, address: e.target.value })}
                        placeholder="Ville, Canton"
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Type de projet *</label>
                    <select
                      value={quoteForm.projectType}
                      onChange={(e) => setQuoteForm({ ...quoteForm, projectType: e.target.value })}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                    >
                      <option value="">Sélectionnez un type de projet</option>
                      <option value="solar">Panneaux solaires</option>
                      <option value="heat-pump">Pompe à chaleur</option>
                      <option value="battery">Batterie de stockage</option>
                      <option value="boiler">Boiler thermodynamique</option>
                      <option value="insulation">Isolation</option>
                      <option value="windows">Fenêtres</option>
                      <option value="complete">Solution complète</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Détails du projet *</label>
                    <textarea
                      value={quoteForm.message}
                      onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                      placeholder="Décrivez votre projet, vos besoins, la surface disponible..."
                      rows={6}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#e57200] focus:outline-none transition-colors"
                    />
                  </div>

                  <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="text-[#e57200] flex-shrink-0 mt-0.5" size={24} />
                      <div className="text-sm text-gray-700">
                        <strong>Gratuit et sans engagement :</strong> Nous vous contacterons rapidement 
                        pour planifier une visite technique et établir un devis personnalisé.
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-[#e57200] text-white rounded-lg font-bold text-lg hover:bg-[#e57200]/90 transition-all shadow-lg hover:shadow-xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Demander mon devis gratuit'}
                    {!isSubmitting && <Send className="ml-2" size={20} />}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-xl p-16 text-center">
            <MapPin className="mx-auto text-[#e57200] mb-4" size={64} />
            <h3 className="text-2xl font-bold text-[#03224c] mb-2">{companyInfo.address}</h3>
            <p className="text-gray-600 font-semibold">Canton de Vaud, Suisse</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
